import type { ReactNode } from "react";
import { cx } from "@/shared/lib/cx";
import { EmptyState, ErrorBanner, Skeleton } from "../feedback/Feedback";
import type { IconName } from "../Icon";
import { Pagination, type PaginationProps } from "./Pagination";

export interface Column<T> {
  /** Stable id (React key). */
  key: string;
  header: ReactNode;
  cell: (row: T, index: number) => ReactNode;
  align?: "start" | "center" | "end";
  /** Keep the cell on one line (dates, ids). */
  nowrap?: boolean;
  className?: string;
  /** Fixed column width (e.g. "1%" for action columns, "120px"). */
  width?: string;
}

export interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: unknown;
  /** Empty-state title, and optional description / call to action / icon. */
  emptyText?: ReactNode;
  emptyDescription?: ReactNode;
  emptyAction?: ReactNode;
  emptyIcon?: IconName;
  onRowClick?: (row: T) => void;
  isRowSelected?: (row: T) => boolean;
  pagination?: PaginationProps;
  /** Number of placeholder rows while loading. */
  skeletonRows?: number;
}

/** Declarative table: columns in, rows out, with loading / error / empty / paging built in. */
export function DataTable<T>({
  columns, rows, rowKey, loading, error, emptyText = "Không có dữ liệu", emptyDescription, emptyAction, emptyIcon = "inbox",
  onRowClick, isRowSelected, pagination, skeletonRows = 5,
}: DataTableProps<T>) {
  const showSkeleton = loading && !rows;
  return (
    <>
      {!!error && <div className="card-body"><ErrorBanner error={error} /></div>}
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>{columns.map((c) => <th key={c.key} style={c.width ? { width: c.width } : undefined} className={c.align && `align-${c.align}`}>{c.header}</th>)}</tr>
          </thead>
          <tbody aria-busy={loading || undefined}>
            {showSkeleton ? (
              Array.from({ length: skeletonRows }, (_, i) => (
                <tr key={i}>{columns.map((c, j) => <td key={c.key}><Skeleton width={j === 0 ? "70%" : "50%"} /></td>)}</tr>
              ))
            ) : !rows?.length ? (
              <tr className="cell-empty">
                <td colSpan={columns.length}>
                  <EmptyState compact icon={emptyIcon} title={emptyText} description={emptyDescription} action={emptyAction} />
                </td>
              </tr>
            ) : rows.map((row, i) => (
              <tr
                key={rowKey(row)}
                className={cx(onRowClick && "clickable", isRowSelected?.(row) && "selected")}
                onClick={onRowClick && ((e) => {
                  // Let links and buttons inside the row do their own thing.
                  if ((e.target as HTMLElement).closest("a, button, input, select, label")) return;
                  onRowClick(row);
                })}
              >
                {columns.map((c) => (
                  <td key={c.key} className={cx(c.align && `align-${c.align}`, c.nowrap && "nowrap", c.className)}>{c.cell(row, i)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pagination && <Pagination {...pagination} />}
    </>
  );
}
